import React, { useState } from 'react';
import { DraftEdit, DraftRow, Flag, ReviewState, View } from '../utils/scorecardApi';
import '../css/scorecard.css';

type Person = { player_id: number; first_name: string; last_name: string };

const matchBadge: Record<string, { text: string; cls: string }> = {
  auto: { text: '✓ Matched', cls: 'ready' },
  confirmed: { text: '✓ Confirmed', cls: 'ready' },
  suggested: { text: 'Best guess — confirm', cls: 'review' },
  unmatched: { text: 'Who is this?', cls: 'review' },
  skipped: { text: 'Skipped', cls: 'holes' },
};

export const isDecided = (row: DraftRow) => ['auto', 'confirmed', 'skipped'].includes(row.assignment.status);

interface PlayerPickProps {
  row: DraftRow;
  total: number | null | undefined;
  roundPlayers: Person[];
  players: Person[];
  canEdit: boolean;
  busy: boolean;
  onEdit: (edits: DraftEdit[]) => void;
  compact?: boolean;
}

/**
 * Who one card row is: the name as written, the best matches, everyone in the
 * round, everyone else ("won't be saved"), or skip — plus "remember this name".
 */
export const PlayerPick: React.FC<PlayerPickProps> = ({ row, total, roundPlayers, players, canEdit, busy, onEdit, compact = false }) => {
  const a = row.assignment;
  const badge = matchBadge[a.status] || matchBadge.unmatched;
  const cand = a.candidates || [];
  const roundIds = new Set(roundPlayers.map((p) => p.player_id));
  const inRound = roundPlayers.filter((p) => !cand.some((c) => c.player_id === p.player_id));
  const others = players.filter((p) => !roundIds.has(p.player_id) && !cand.some((c) => c.player_id === p.player_id));
  const selected = a.status === 'skipped' ? 'skip' : a.player_id != null ? String(a.player_id) : '';
  const why = cand.find((c) => c.player_id === a.player_id)?.reasons;
  const chosen = players.find((p) => p.player_id === a.player_id) || roundPlayers.find((p) => p.player_id === a.player_id);
  return (
    <div className={`sc-player-row${compact ? ' sc-player-row--compact' : ''}`}>
      <div className="sc-player-card">
        <strong>“{row.name_text ?? '?'}”</strong>{row.initials && <span className="sc-note"> ({row.initials})</span>}
        <span className="sc-note"> · {total ?? '?'} strokes</span>
        <div><span className={`sc-chip-status sc-chip-status--${badge.cls}`}>{badge.text}</span></div>
      </div>
      <div className="sc-player-pick">
        <select
          value={selected}
          disabled={!canEdit || busy}
          aria-label={`Who is “${row.name_text ?? row.row_id}”`}
          onChange={(e) => {
            const val = e.target.value;
            if (!val) return;
            onEdit([val === 'skip'
              ? { type: 'assign', row_id: row.row_id, skip: true }
              : { type: 'assign', row_id: row.row_id, player_id: Number(val), remember_alias: a.remember_alias !== false }]);
          }}
        >
          <option value="">Choose a player…</option>
          {cand.length > 0 && (
            <optgroup label="Best matches">
              {cand.map((c) => <option key={c.player_id} value={c.player_id}>{c.name}{c.in_round ? '' : ' (not in this round)'}</option>)}
            </optgroup>
          )}
          {inRound.length > 0 && (
            <optgroup label="In this round">
              {inRound.map((p) => <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}</option>)}
            </optgroup>
          )}
          <optgroup label="Not in this round (won't be saved)">
            {others.map((p) => <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}</option>)}
          </optgroup>
          <option value="skip">Skip this row — don't save it</option>
        </select>
        {a.status === 'suggested' && a.player_id != null && (
          <button type="button" className="sc-btn sc-btn--sm sc-btn--primary" disabled={!canEdit || busy}
            onClick={() => onEdit([{ type: 'assign', row_id: row.row_id, player_id: a.player_id!, remember_alias: a.remember_alias !== false }])}>
            Confirm{chosen ? ` ${chosen.first_name}` : ''}
          </button>
        )}
      </div>
      {why && a.status !== 'skipped' && <div className="sc-note sc-why">{why.join(' · ')}</div>}
      {row.name_text && a.player_id != null && a.status !== 'skipped' && row.name_text.replace(/\s/g, '').length > 1 && (
        <label className="sc-note sc-remember">
          <input
            type="checkbox"
            checked={a.remember_alias !== false}
            disabled={!canEdit || busy}
            onChange={(e) => onEdit([{ type: 'assign', row_id: row.row_id, player_id: a.player_id!, remember_alias: e.target.checked }])}
          />
          Remember “{row.name_text}” as {chosen?.first_name ?? 'this player'}
        </label>
      )}
    </div>
  );
};

interface ScorecardTasksProps {
  state: ReviewState;
  view: View;
  canEdit: boolean;
  busy: boolean;
  onEdit: (edits: DraftEdit[]) => void;
  /** Holes with something to fix (red in the score grid). */
  problemHoles: number;
  onFixHoles: () => void;
  /** Blocking checks that aren't about a player or a hole (gross, existing scores, …). */
  otherFlags: Flag[];
  flagActions: (f: Flag) => React.ReactNode[];
  onCommit: () => void;
  committing: boolean;
}

/**
 * The review as three steps, in order: who's who, then the holes, then save.
 * Shown at the top of the chat on phones, so the reviewer sees what's left
 * before reading the assistant's messages.
 */
const ScorecardTasks: React.FC<ScorecardTasksProps> = ({
  state, view, canEdit, busy, onEdit, problemHoles, onFixHoles, otherFlags, flagActions, onCommit, committing,
}) => {
  const draft = state.draft!;
  const [showAll, setShowAll] = useState(false);
  const roundPlayers = state.round?.players || [];
  const total = (rowId: string) => view.rows.find((r) => r.row_id === rowId)?.total;
  const playerFlags = view.flags.filter((f) => f.blocking && (f.type === 'needs_player' || f.type === 'duplicate_player'));
  const undecided = draft.rows.filter((r) => !isDecided(r));
  const playersDone = undecided.length === 0 && playerFlags.length === 0;
  const holesLeft = problemHoles + otherFlags.length;
  const step = !playersDone ? 1 : holesLeft > 0 ? 2 : 3;
  const nameOf = (row: DraftRow) => {
    const p = state.players.find((x) => x.player_id === row.assignment.player_id);
    return row.assignment.status === 'skipped' ? 'skipped' : p ? `${p.first_name} ${p.last_name}` : '?';
  };
  const cls = (n: number) => `sc-step ${step === n ? 'sc-step--active' : step > n ? 'sc-step--done' : 'sc-step--todo'}`;

  return (
    <section className="sc-tasks" aria-label="Review steps">
      <ol className="sc-steps">
        <li className={cls(1)}>
          <div className="sc-step-head">
            <span className="sc-step-num">{step > 1 ? '✓' : '1'}</span>
            <span className="sc-step-title">Who's who</span>
            <span className="sc-step-meta">{draft.rows.length - undecided.length} of {draft.rows.length} players set</span>
          </div>
          {step === 1 && (
            <div className="sc-step-body">
              {playerFlags.filter((f) => f.type === 'duplicate_player').map((f) => <p key={f.id} className="sc-error" style={{ margin: 0 }}>{f.message}</p>)}
              {undecided.map((row) => (
                <PlayerPick key={row.row_id} row={row} total={total(row.row_id)} roundPlayers={roundPlayers} players={state.players} canEdit={canEdit} busy={busy} onEdit={onEdit} compact />
              ))}
            </div>
          )}
          {draft.rows.some(isDecided) && (
            <div className="sc-step-body">
              <button type="button" className="sc-linkbtn" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
                {showAll ? 'Hide' : 'Show'} matched players
              </button>
              {!showAll && (
                <p className="sc-note" style={{ margin: '4px 0 0' }}>
                  {draft.rows.filter(isDecided).map((r) => `“${r.name_text ?? '?'}” → ${nameOf(r)}`).join(' · ')}
                </p>
              )}
              {showAll && draft.rows.filter(isDecided).map((row) => (
                <PlayerPick key={row.row_id} row={row} total={total(row.row_id)} roundPlayers={roundPlayers} players={state.players} canEdit={canEdit} busy={busy} onEdit={onEdit} compact />
              ))}
            </div>
          )}
        </li>

        <li className={cls(2)}>
          <div className="sc-step-head">
            <span className="sc-step-num">{step > 2 ? '✓' : '2'}</span>
            <span className="sc-step-title">Check the scores</span>
            <span className="sc-step-meta">
              {holesLeft === 0 ? 'nothing to fix' : `${problemHoles} hole${problemHoles === 1 ? '' : 's'}${otherFlags.length ? ` + ${otherFlags.length} other` : ''} to look at`}
            </span>
          </div>
          {step === 2 && (
            <div className="sc-step-body">
              {problemHoles > 0 && (
                <>
                  <p className="sc-note" style={{ margin: 0 }}>They're red in the scores. Each one shows what's wrong — compare it with the photo, then fix it or tap “Looks right”.</p>
                  <button type="button" className="sc-btn sc-btn--primary sc-btn--block" onClick={onFixHoles}>
                    Fix {problemHoles === 1 ? 'the hole' : `the ${problemHoles} holes`} →
                  </button>
                </>
              )}
              {otherFlags.length > 0 && (
                <ul className="sc-flags">
                  {otherFlags.map((f) => (
                    <li key={f.id} className={`sc-flag sc-flag--${f.severity}`}>
                      <span className="sc-flag-msg">{f.message}</span>
                      <span className="sc-flag-actions">{flagActions(f)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {step === 1 && holesLeft > 0 && <p className="sc-note sc-step-body" style={{ margin: 0 }}>Next, once the players are set.</p>}
        </li>

        <li className={cls(3)}>
          <div className="sc-step-head">
            <span className="sc-step-num">3</span>
            <span className="sc-step-title">Save to the round</span>
          </div>
          {step === 3 && (
            <div className="sc-step-body">
              <p className="sc-ok" style={{ margin: 0 }}>
                ✓ Everything checks out{view.eligible_count ? ` — ${view.eligible_count} player${view.eligible_count === 1 ? '' : 's'} ready to save.` : '.'}
              </p>
              {canEdit && (
                <button type="button" className="sc-btn sc-btn--primary sc-btn--block" onClick={onCommit} disabled={!view.can_commit || busy}>
                  {committing ? 'Saving…' : 'Save to round'}
                </button>
              )}
            </div>
          )}
        </li>
      </ol>
    </section>
  );
};

export default ScorecardTasks;
